import { useCallback, useMemo, useState } from 'react'
import { Archive, ArrowLeft, CheckCircle2, ChevronDown, Play, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CompleteCycleDialog } from './CompleteCycleDialog'
import { PRIORITY_ICONS } from './priority'
import { BurnUpChart } from './BurnUpChart'
import { CYCLE_TASK_DRAG_TYPE, CycleBacklogPanel } from './CycleBacklogPanel'
import { computeCycleProgress, computeUsualCapacity } from './cycleMetrics'
import type { CycleDocument } from '@/lib/db/rxdb/schemas/cycle'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useCycle, useCycleMutations, useProjectCycles } from '@/lib/db/rxdb/hooks/entities/cycles'
import { StarToggle } from '@/components/app/StarToggle'
import {
  useCycleWorkItemMutations,
  useCycleWorkItems,
  useCycleWorkItemsByCycle,
} from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useProject, useTask, useTasks } from '@/lib/db/rxdb/hooks'
import { useMembers } from '@/lib/db/rxdb/hooks/entities/members'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { activateCycle } from '@/lib/api/features/cycles/api'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { TaskDetailsDialog } from '@/routes/features/tasks/components/TaskDetailsDialog'
import { useTaskListNavigation } from '@/routes/features/tasks/hooks/useTaskListNavigation'
import { groupTasksByStatus } from '@/routes/features/tasks/utils/groupTasksByStatus'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import { isClosedProgressType } from '@/routes/features/tasks/constants'
import useLoginStore from '@/store/LoginStore'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { getDueDateColorClass } from '@/routes/features/tasks/utils/datetime'

interface CycleDetailViewProps {
  cycleId: string
  projectId: string
  onBack: () => void
  onComplete?: () => void
}

/**
 * One cycle (design 8b / 8c). A draft is a planning surface: the cycle on the
 * left, the backlog beside it, a capacity bar filling as you add work. An
 * active or completed cycle shows its work grouped by status next to a
 * burn-up.
 */
export function CycleDetailView({ cycleId, projectId, onBack, onComplete }: CycleDetailViewProps) {
  const { t } = useTranslation('projects')
  const { t: tCommon } = useTranslation('common')
  const { date: formatDate } = useDateTimeFormatters()
  const { cycle, isLoading } = useCycle(cycleId)
  const { project } = useProject(projectId)
  const { cycles: projectCyclesOrNull } = useProjectCycles(projectId)
  const { cycleWorkItems: cycleWorkItemsOrNull } = useCycleWorkItemsByCycle(cycleId)
  const { cycleWorkItems: allWorkItemsOrNull } = useCycleWorkItems() // never null for the unfiltered query
  const projectCycles = useMemo(() => projectCyclesOrNull ?? [], [projectCyclesOrNull])
  const cycleWorkItems = useMemo(() => cycleWorkItemsOrNull ?? [], [cycleWorkItemsOrNull])
  const allWorkItems = allWorkItemsOrNull
  const { tasks } = useTasks()
  const { deleteCycle, updateCycle } = useCycleMutations()
  const { createCycleWorkItem, deleteCycleWorkItem } = useCycleWorkItemMutations()
  const { members } = useMembers()
  const { all: allStatusGroup } = useStatuses()
  const [showComplete, setShowComplete] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [isDropTarget, setIsDropTarget] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { task: selectedTask } = useTask(selectedTaskId ?? undefined)
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const statusList = allStatusGroup.statuses
  const closedStatusIds = useMemo(
    () => new Set(statusList.filter((s) => isClosedProgressType(s.progressType)).map((s) => s.id)),
    [statusList],
  )

  const cycleTasks = useMemo(() => {
    const workItemIds = new Set(cycleWorkItems.map((cwi) => cwi.workItemId))
    return tasks.filter((task) => workItemIds.has(task.id) && !task.isDeleted && task.projectId === projectId)
  }, [cycleWorkItems, tasks, projectId])

  // Backlog: open, top-level project tasks not planned into any open cycle.
  const backlogTasks = useMemo(() => {
    const openCycleIds = new Set(projectCycles.filter((c) => c.status !== 'completed').map((c) => c.id))
    const planned = new Set(
      allWorkItems.filter((w) => !w.isDeleted && openCycleIds.has(w.cycleId)).map((w) => w.workItemId),
    )
    return tasks.filter(
      (task) =>
        task.projectId === projectId &&
        !task.isDeleted &&
        !task.isArchived &&
        !task.parentId &&
        !closedStatusIds.has(task.statusId) &&
        !planned.has(task.id),
    )
  }, [projectCycles, allWorkItems, tasks, projectId, closedStatusIds])

  const capacity = useMemo(() => {
    const completed = [...projectCycles]
      .filter((c) => c.status === 'completed')
      .sort((a, b) => a.number - b.number)
    return computeUsualCapacity(
      completed.map((c) => allWorkItems.filter((w) => w.cycleId === c.id)),
    )
  }, [projectCycles, allWorkItems])

  const progress = useMemo(
    () =>
      cycle
        ? computeCycleProgress({ cycle, workItems: cycleWorkItems, tasks: cycleTasks, closedStatusIds })
        : null,
    [cycle, cycleWorkItems, cycleTasks, closedStatusIds],
  )

  const statusGroups = useMemo(() => groupTasksByStatus(cycleTasks, statusList), [cycleTasks, statusList])

  const memberMap = useMemo(() => {
    const map = new Map<string, (typeof members)[number]>()
    members.forEach((m) => { if (!m.isDeleted) map.set(m.id, m) })
    return map
  }, [members])

  const resolveIdentifier = useCallback(
    (task: TaskDocument) => resolveTaskIdentifier(task, project?.projectKey),
    [project?.projectKey],
  )

  const handleTaskClick = useCallback((taskId: string) => {
    setSelectedTaskId(taskId)
    setIsDialogOpen(true)
  }, [])

  const { handleNavigateUp, handleNavigateDown, canNavigateUp, canNavigateDown } =
    useTaskListNavigation(cycleTasks, selectedTaskId, setSelectedTaskId)

  const handleDialogChange = useCallback((open: boolean) => {
    setIsDialogOpen(open)
    if (!open) setSelectedTaskId(null)
  }, [])

  const handleAddTask = async (taskId: string) => {
    if (!workspaceId) return
    if (cycleWorkItems.some((cwi) => cwi.workItemId === taskId)) return
    try {
      await createCycleWorkItem({
        envId: workspaceId,
        cycleId,
        workItemId: taskId,
        statusIdAtClose: null,
        progressTypeAtClose: null,
        addedAt: Date.now(),
        sourceCycleId: null,
      })
    } catch {
      toast.error(t('cycles.toasts.addFailed'))
    }
  }

  const handleActivate = async () => {
    if (!workspaceId) return
    setIsActivating(true)
    try {
      await activateCycle(workspaceId, cycleId)
      await updateCycle(cycleId, { status: 'active' })
      toast.success(t('cycles.toasts.activated'))
    } catch {
      toast.error(t('cycles.toasts.activateFailed'))
    } finally {
      setIsActivating(false)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteCycle(cycleId)
      toast.success(t('cycles.toasts.deleted'))
      onBack()
    } catch {
      toast.error(t('cycles.toasts.deleteFailed'))
    }
  }

  const handleRemoveTask = async (workItemId: string) => {
    const cwi = cycleWorkItems.find((c) => c.workItemId === workItemId)
    if (cwi) {
      await deleteCycleWorkItem(cwi.id)
      toast.success(t('cycles.toasts.taskRemoved'))
    }
  }

  if (isLoading || !cycle) return null

  const isDraft = cycle.status === 'draft'
  const dateRange = `${formatDate(new Date(cycle.startDate), { month: 'short', day: 'numeric' })} – ${formatDate(new Date(cycle.endDate), { month: 'short', day: 'numeric' })}`

  const renderRow = (task: TaskDocument & { status?: { progressType?: string } | null; userName?: string | null }) => {
    const priorityEntry = PRIORITY_ICONS[task.priority ?? 0]
    const member = task.userId ? memberMap.get(task.userId) : undefined
    return (
      <li
        key={task.id}
        className="group flex cursor-pointer items-center gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-secondary/40"
        onClick={() => handleTaskClick(task.id)}
      >
        {task.status?.progressType && (
          <StatusIcon progressType={task.status.progressType} statusId={task.statusId} size="sm" className="shrink-0" />
        )}
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{resolveIdentifier(task)}</span>
        <span className={cn('min-w-0 flex-1 truncate text-sm', task.isArchived && 'text-muted-foreground line-through')}>
          {task.name}
        </span>
        {task.isArchived && (
          <Badge variant="secondary" className="shrink-0 gap-1">
            <Archive />
          </Badge>
        )}
        {task.priority != null && task.priority > 0 && priorityEntry && (
          <priorityEntry.Icon className={cn('size-3.5 shrink-0', priorityEntry.color)} />
        )}
        {task.dueDate && (
          <span className={cn('shrink-0 text-xs', getDueDateColorClass(task.dueDate))}>
            {formatDate(new Date(task.dueDate), { month: 'short', day: 'numeric' })}
          </span>
        )}
        {memberAvatarSrc(member) ? (
          <img
            src={memberAvatarSrc(member)}
            alt={member?.name ?? task.userName ?? ''}
            className="size-5 shrink-0 rounded-full object-cover"
          />
        ) : task.userName ? (
          <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium uppercase">
            {task.userName.charAt(0)}
          </div>
        ) : null}
        {cycle.status !== 'completed' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              void handleRemoveTask(task.id)
            }}
            aria-label={t('cycles.remove')}
            title={t('cycles.remove')}
            className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition hover:bg-secondary hover:text-destructive group-hover:opacity-100"
          >
            <X className="size-3.5" />
          </button>
        )}
      </li>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 pb-10 pt-6 md:px-8">
      <button
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        {t('cycles.backToCycles')}
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-[26px] font-bold tracking-[-0.015em]">{cycle.name}</h2>
            <CycleStatusPill cycle={cycle} workingDay={progress?.workingDay} totalWorkingDays={progress?.totalWorkingDays} />
            <StarToggle entityType="cycle" entityId={cycle.id} />
          </div>
          <p className="text-sm text-muted-foreground">{dateRange}</p>
          {cycle.description && (
            <p className="mt-1 text-sm">
              <span className="text-muted-foreground">{t('cycles.goal')}: </span>
              {cycle.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <Button size="sm" onClick={() => void handleActivate()} disabled={isActivating}>
              <Play className="mr-1.5 size-3.5" />
              {isActivating ? t('cycles.activating') : t('cycles.activate', { name: cycle.name })}
            </Button>
          )}
          {cycle.status === 'active' && (
            <Button size="sm" onClick={() => setShowComplete(true)}>
              <CheckCircle2 className="mr-1.5 size-3.5" />
              {t('cycles.complete')}
            </Button>
          )}
          {cycle.status !== 'completed' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              aria-label={t('cycles.deleteDialog.title', { name: cycle.name })}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
        </div>
      </div>

      {isDraft ? (
        /* Planning (8b) */
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(CYCLE_TASK_DRAG_TYPE)) return
              e.preventDefault()
              setIsDropTarget(true)
            }}
            onDragLeave={() => setIsDropTarget(false)}
            onDrop={(e) => {
              setIsDropTarget(false)
              const taskId = e.dataTransfer.getData(CYCLE_TASK_DRAG_TYPE)
              if (taskId) void handleAddTask(taskId)
            }}
            className={cn('min-w-0 rounded-xl transition-colors', isDropTarget && 'bg-brand-tint/40 ring-2 ring-brand/40')}
          >
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="text-[15px] font-semibold">
                {t('cycles.inThisCycle')}{' '}
                <span className="font-normal text-muted-foreground">{t('cycles.tasks', { count: cycleTasks.length })}</span>
              </h3>
            </div>
            <CapacityBar planned={cycleTasks.length} capacity={capacity} />
            {cycleTasks.length === 0 ? (
              <div className="mt-3 rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
                {isDropTarget ? t('cycles.dropHere', { name: cycle.name }) : t('cycles.emptyCycle')}
              </div>
            ) : (
              <ul className="mt-3 divide-y overflow-hidden rounded-lg border bg-card">
                {cycleTasks.map(renderRow)}
              </ul>
            )}
          </section>
          <div className="lg:sticky lg:top-0 lg:max-h-[calc(100svh-8rem)] lg:self-start">
            <CycleBacklogPanel
              tasks={backlogTasks}
              onAdd={(id) => void handleAddTask(id)}
              resolveIdentifier={resolveIdentifier}
              projectName={project?.name}
            />
          </div>
        </div>
      ) : (
        /* Running / done (8c) */
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-4">
            {statusGroups.length === 0 && (
              <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
                {t('cycles.emptyCycle')}
              </div>
            )}
            {statusGroups.map((group) => (
              <StatusGroup
                key={group.status.id}
                title={group.status.name}
                progressType={group.status.progressType}
                count={group.tasks.length}
                defaultOpen={!group.isClosed || cycle.status === 'completed'}
              >
                <ul className="divide-y overflow-hidden rounded-lg border bg-card">{group.tasks.map(renderRow)}</ul>
              </StatusGroup>
            ))}
          </div>
          {progress && (
            <aside className="self-start rounded-xl border bg-card p-4">
              <h3 className="mb-1 text-[13px] font-semibold">{t('cycles.progress')}</h3>
              {cycle.status === 'active' && (
                <p className="mb-3 text-xs text-muted-foreground">
                  {t('cycles.daysLeft', { count: progress.workingDaysLeft })}
                </p>
              )}
              <div className="mb-3 flex items-baseline gap-2">
                <span className="text-[22px] font-bold tabular-nums">
                  {progress.done}/{progress.scope}
                </span>
                <span className="text-xs text-muted-foreground">{t('cycles.chartDone').toLowerCase()}</span>
              </div>
              <BurnUpChart
                series={progress.series}
                totalDays={progress.totalDays}
                scope={progress.scope}
                labels={{ done: t('cycles.chartDone'), scope: t('cycles.chartScope'), ideal: t('cycles.chartIdeal') }}
              />
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11.5px]">
                {progress.addedMidCycle > 0 && (
                  <span className="rounded-md bg-secondary px-2 py-0.5 text-secondary-foreground">
                    {t('cycles.addedMid', { count: progress.addedMidCycle })}
                  </span>
                )}
                {cycle.status === 'active' &&
                  (progress.behindBy > 0 ? (
                    <span className="rounded-md bg-brand-tint px-2 py-0.5 text-brand-tint-foreground">
                      {t('cycles.behindBy', { count: progress.behindBy })}
                    </span>
                  ) : (
                    <span className="rounded-md bg-secondary px-2 py-0.5 text-secondary-foreground">
                      {t('cycles.onTrack')}
                    </span>
                  ))}
              </div>
            </aside>
          )}
        </div>
      )}

      <CompleteCycleDialog
        cycleId={cycleId}
        projectId={projectId}
        open={showComplete}
        onOpenChange={setShowComplete}
        onComplete={onComplete}
      />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cycles.deleteDialog.title', { name: cycle.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cycles.deleteDialog.description', { count: cycleWorkItems.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { void handleDelete() }}
            >
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TaskDetailsDialog
        task={selectedTask ?? null}
        statuses={statusList}
        open={isDialogOpen}
        onOpenChange={handleDialogChange}
        onNavigateUp={handleNavigateUp}
        onNavigateDown={handleNavigateDown}
        canNavigateUp={canNavigateUp}
        canNavigateDown={canNavigateDown}
      />
    </div>
  )
}

function CycleStatusPill({
  cycle,
  workingDay,
  totalWorkingDays,
}: {
  cycle: CycleDocument
  workingDay?: number
  totalWorkingDays?: number
}) {
  const { t } = useTranslation('projects')
  const label = t(`cycles.status.${cycle.status}`)
  return (
    <span
      className={cn(
        'rounded-md px-2 py-0.5 text-[11.5px] font-medium',
        cycle.status === 'active' ? 'bg-brand-tint text-brand-tint-foreground' : 'bg-secondary text-secondary-foreground',
      )}
    >
      {cycle.status === 'active' && workingDay && totalWorkingDays
        ? `${label} · ${t('cycles.dayOf', { day: workingDay, total: totalWorkingDays })}`
        : label}
    </span>
  )
}

/** Planned work against the usual range of completed work (design 8b). */
function CapacityBar({ planned, capacity }: { planned: number; capacity: { low: number; high: number } | null }) {
  const { t } = useTranslation('projects')
  if (!capacity) {
    return <p className="text-xs text-muted-foreground">{t('cycles.noCapacityHistory')}</p>
  }
  const max = Math.max(capacity.high, planned, 1)
  const over = planned > capacity.high
  return (
    <div>
      <div className="relative h-2 overflow-hidden rounded-full bg-secondary">
        {/* the usual range */}
        <div
          className="absolute inset-y-0 bg-foreground/10"
          style={{ left: `${(capacity.low / max) * 100}%`, width: `${((capacity.high - capacity.low) / max) * 100}%` }}
        />
        <div
          className={cn('absolute inset-y-0 left-0 rounded-full transition-[width]', over ? 'bg-brand' : 'bg-foreground/70')}
          style={{ width: `${(planned / max) * 100}%` }}
        />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {capacity.low === capacity.high
          ? t('cycles.usualCapacitySingle', { count: capacity.high })
          : t('cycles.usualCapacity', { low: capacity.low, high: capacity.high })}
      </p>
    </div>
  )
}

function StatusGroup({
  title,
  progressType,
  count,
  defaultOpen,
  children,
}: {
  title: string
  progressType: string
  count: number
  defaultOpen: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-2 flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] font-semibold hover:bg-muted"
      >
        <ChevronDown className={cn('size-3.5 text-muted-foreground transition-transform', !open && '-rotate-90')} />
        <StatusIcon progressType={progressType} size="sm" />
        {title}
        <span className="text-xs font-medium tabular-nums text-muted-foreground">{count}</span>
      </button>
      {open && children}
    </section>
  )
}
